// src/features/trades/reviewHelpers.js
//
// Pure helpers for trade decisions and skip outcomes: the Review-trade screen,
// the R formatter shared by Today and Decision Quality, and the Counterfactual
// chart's tooltip. Kept side-effect free and dependency-free so they can be
// unit-tested without React/Redux.

/**
 * Compute the planned reward-to-risk ratio for a trade.
 *
 * R/R = |takeProfit - entryPrice| / |entryPrice - initialStop|
 *
 * Returns `null` when the inputs are missing or the denominator is zero (i.e.
 * stop equals entry, which would imply infinite leverage). Callers should
 * render '—' for null to avoid leaking Infinity/NaN into the UI.
 *
 * @param {number} entryPrice
 * @param {number} initialStop
 * @param {number} takeProfit
 * @returns {number | null}
 */
export const computeRiskReward = (entryPrice, initialStop, takeProfit) => {
  // Number(null) === 0 and Number('') === 0, which would silently pass the
  // isFinite check below — reject those explicitly so missing inputs don't
  // produce a 0R answer that looks legitimate.
  if (
    [entryPrice, initialStop, takeProfit].some((v) => v === null || v === undefined || v === '')
  ) {
    return null;
  }
  const e = Number(entryPrice);
  const s = Number(initialStop);
  const t = Number(takeProfit);
  if (!Number.isFinite(e) || !Number.isFinite(s) || !Number.isFinite(t)) {
    return null;
  }
  const denom = Math.abs(e - s);
  if (denom === 0) {
    return null;
  }
  return Math.abs(t - e) / denom;
};

/**
 * Compute the absolute risk amount for a planned trade — i.e. the per-share
 * stop distance multiplied by the position size. Returned in the same
 * currency as `entryPrice`.
 *
 * Returns `null` when any input is non-numeric so the UI can degrade gracefully.
 *
 * @param {number} entryPrice
 * @param {number} initialStop
 * @param {number} positionSize
 * @returns {number | null}
 */
export const computeRiskAmount = (entryPrice, initialStop, positionSize) => {
  if (
    entryPrice === null || entryPrice === undefined ||
    initialStop === null || initialStop === undefined ||
    positionSize === null || positionSize === undefined
  ) {
    return null;
  }
  const e = Number(entryPrice);
  const s = Number(initialStop);
  const q = Number(positionSize);
  if (!Number.isFinite(e) || !Number.isFinite(s) || !Number.isFinite(q)) {
    return null;
  }
  return Math.abs(e - s) * q;
};

/**
 * Format a numeric amount with a currency code/symbol. Uses Intl.NumberFormat
 * so we get locale-aware grouping & decimal handling. Falls back to a plain
 * "$" prefix when Intl can't satisfy the currency request (e.g. unknown code).
 *
 * @param {number} amount
 * @param {string} [currency='USD']
 * @returns {string}
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (!Number.isFinite(amount)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/**
 * Render a short human-readable "time since" string from an ISO timestamp.
 * Used in the Review-trade header ("Fired 2 hours ago") so the user can tell
 * at a glance how fresh the signal is.
 *
 * Rounds down; uses singular/plural correctly; falls back to a calendar date
 * for events more than a week old.
 *
 * @param {string | Date} input
 * @param {Date} [now=new Date()] - injectable for tests
 * @returns {string}
 */
export const formatRelativeTime = (input, now = new Date()) => {
  if (!input) {
    return '';
  }
  const then = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(then.getTime())) {
    return '';
  }
  const diffMs = now.getTime() - then.getTime();
  const seconds = Math.max(0, Math.floor(diffMs / 1000));
  if (seconds < 60) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  // > 1 week — fall back to an absolute date
  return then.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Canonical skip-reason enum, mirrored from the backend. Multi-select — a skip
 * can carry several of these. The order here is the order the checkboxes render.
 * Labels are strategy-agnostic ("anchor", not "cycle low pivot") so the list
 * works for any strategy, not just DCL.
 */
export const SKIP_REASONS = [
  {
    value: 'entry_extended_from_anchor',
    label: 'Entry too extended from the setup anchor',
  },
  {
    value: 'awaiting_confirmation',
    label: 'Awaiting confirmation — waiting for a trigger (e.g. trendline break)',
  },
  {
    value: 'risk_reward_poor',
    label: 'Stop too wide / target unreachable — poor risk:reward',
  },
  {
    value: 'thesis_already_played_out',
    label: 'Move has already played out',
  },
  {
    value: 'low_conviction_signal',
    label: 'Low conviction / weak signal score',
  },
  {
    value: 'regime_risk_off',
    label: 'Market regime / leader risk-off',
  },
  {
    value: 'correlated_exposure',
    label: 'Already exposed to this sector / correlated position',
  },
  {
    value: 'account_drawdown_pause',
    label: 'Account in drawdown — pausing new entries',
  },
  {
    value: 'broker_access',
    label: 'Broker can\'t trade this symbol',
  },
  {
    value: 'other',
    label: 'Other reason (specify)',
  },
];

/**
 * Canonical confirm-reason enum, mirrored from the backend — the symmetric
 * twin of SKIP_REASONS: WHY a planned trade was taken. Optional server-side
 * (quick confirms still work untagged), but every untagged confirm is
 * decision-analytics data lost, so the review screen always offers these.
 */
export const CONFIRM_REASONS = [
  {
    value: 'fresh_cycle_entry',
    label: 'Fresh entry — close to the cycle low / anchor',
  },
  {
    value: 'trendline_break_confirmed',
    label: 'Structural trigger confirmed (e.g. trendline break + hold)',
  },
  {
    value: 'cluster_confluence',
    label: 'Cluster confluence — multiple symbols/timeframes agree',
  },
  {
    value: 'regime_aligned',
    label: 'Market / sector regime supports the trade',
  },
  {
    value: 'risk_reward_attractive',
    label: 'Tight invalidation vs target — attractive risk:reward',
  },
  {
    value: 'adding_to_winner',
    label: 'Adding to an existing winning position',
  },
  {
    value: 'other',
    label: 'Other reason (specify)',
  },
];

/**
 * Deprecated slugs no longer offered in the dropdown but kept valid server-side
 * as aliases — mapped here so historical records still render a friendly label.
 */
const LEGACY_SKIP_REASON_LABELS = {
  entry_too_far_from_pivot: 'Entry too extended from the setup anchor',
};

/**
 * Look up the friendly label for a skipReason enum value. Falls back to a
 * legacy alias label, then the raw value, so old records and reasons added
 * server-side ahead of a UI deploy still render something readable.
 */
export const getSkipReasonLabel = (value) => {
  const found = SKIP_REASONS.find((r) => r.value === value);
  if (found) return found.label;
  return LEGACY_SKIP_REASON_LABELS[value] || value;
};

/**
 * Friendly labels for a multi-select skip — accepts the new `skipReasons[]`
 * array and falls back to a single `skipReason` for older records.
 */
export const getSkipReasonLabels = (skipReasons, skipReason) => {
  const values = Array.isArray(skipReasons) && skipReasons.length
    ? skipReasons
    : (skipReason ? [skipReason] : []);
  return values.map(getSkipReasonLabel);
};

export const getConfirmReasonLabel = (value) => {
  const found = CONFIRM_REASONS.find((r) => r.value === value);
  return found ? found.label : value;
};

/**
 * Friendly labels for a multi-select confirm — accepts `confirmReasons[]`
 * and falls back to a single `confirmReason`, mirroring the skip helper.
 */
export const getConfirmReasonLabels = (confirmReasons, confirmReason) => {
  const values = Array.isArray(confirmReasons) && confirmReasons.length
    ? confirmReasons
    : (confirmReason ? [confirmReason] : []);
  return values.map(getConfirmReasonLabel);
};

/**
 * Build the POST body for confirming a planned trade. Only includes fields
 * the user actually changed from the planned defaults — the backend falls
 * back to the planned values for any omitted field, and stamps entryAt to
 * "now" with full-second precision if it isn't supplied.
 *
 * Returns either `{ body }` on success or `{ error: { field, message } }`
 * on a validation failure so the caller can surface it inline.
 *
 * @param {{
 *   overrideEntryPrice: string,
 *   plannedEntryPrice: number | string | null,
 *   overrideEntryAt: string,
 *   seedEntryAt: string,
 *   overridePositionSize: string,
 *   plannedPositionSize: number | string | null,
 * }} params
 */
export const buildConfirmOverrides = ({
  overrideEntryPrice,
  plannedEntryPrice,
  overrideEntryAt,
  seedEntryAt,
  overridePositionSize,
  plannedPositionSize,
}) => {
  const body = {};

  if (overrideEntryPrice !== '' && overrideEntryPrice !== String(plannedEntryPrice)) {
    const parsed = Number(overrideEntryPrice);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return { error: { field: 'entryPrice', message: 'Entry price must be a positive number.' } };
    }
    body.entryPrice = parsed;
  }

  // Only include entryAt when the user actually edited it. Compare to the
  // SEEDED value (what the form was pre-populated with on load) — a no-edit
  // confirm leaves the field equal to its seed and we want the backend to
  // apply its own "now" with full second precision, not our minute-truncated
  // datetime-local value.
  if (overrideEntryAt && overrideEntryAt !== seedEntryAt) {
    const iso = new Date(overrideEntryAt).toISOString();
    body.entryAt = iso;
  }

  if (overridePositionSize !== '' && overridePositionSize !== String(plannedPositionSize)) {
    const parsed = Number(overridePositionSize);
    if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
      return { error: { field: 'positionSize', message: 'Position size must be a positive integer.' } };
    }
    body.positionSize = parsed;
  }

  return { body };
};

export const SKIP_STATUS_LABELS = {
  would_be_stopped: 'Would be stopped',
  would_hit_target: 'Would hit target',
  would_exit_signal: 'Strategy would exit',
  running: 'Still running',
};

/**
 * Verdict presentation for a counterfactual skip outcome (from
 * GET /trades/skip-outcomes). Goodness is judged by savedR — positive means
 * the skip saved money regardless of HOW the ghost trade resolved.
 */

export const getSkipVerdict = (outcome) => {
  if (!outcome) return null;
  const labels = SKIP_STATUS_LABELS;
  const savedR = Number(outcome.savedR) || 0;
  const tone = savedR > 0.05 ? 'good' : savedR < -0.05 ? 'bad' : 'neutral';
  const sign = savedR > 0 ? '+' : '';
  return {
    label: labels[outcome.status] || outcome.status,
    tone, // 'good' = skip saved R, 'bad' = skip cost R, 'neutral' = flat
    savedRText: `${sign}${savedR.toFixed(2)}R ${savedR >= 0 ? 'saved' : 'missed'}`,
    color: tone === 'good' ? 'success' : tone === 'bad' ? 'error' : 'default',
  };
};

export const fmtR = (r) => (r == null ? '—' : `${r > 0 ? '+' : ''}${Number(r).toFixed(2)}R`);

// GET /trades/skip-outcomes series points carry savedR but no status, so the
// Counterfactual chart finds it on the outcome row the point was built from.
// skippedAt is a full timestamp, so skippedAt + symbol nearly always names one
// skip; a key shared by skips that resolved differently gets no status rather
// than the wrong one.
export const skipStatusLookup = (outcomes) => {
  const key = (o) => `${o.skippedAt}|${o.symbol}`;
  const byKey = new Map();
  outcomes.filter((o) => o.skippedAt).forEach((o) => {
    const k = key(o);
    byKey.set(k, byKey.has(k) && byKey.get(k) !== o.status ? null : o.status);
  });
  return (point) => byKey.get(key(point)) ?? undefined;
};

/**
 * Tooltip for one point on the Counterfactual "Cumulative R saved" chart. The
 * running total alone reads like the hovered skip's own result, so the skip's
 * own savedR leads. A de-duped total the API did not send shows as "—", never
 * as the raw figure.
 */
export const skipTooltip = (point, { status, hasDeduped } = {}) => {
  const statusLabel = SKIP_STATUS_LABELS[status];
  return {
    title: `${point.skippedAt ? String(point.skippedAt).slice(0, 10) : '—'} · ${point.symbol}`,
    lines: [
      `This skip: ${fmtR(point.savedR)}${statusLabel ? ` (${statusLabel.toLowerCase()})` : ''}`,
      ...(point.isDuplicate ? ['Concurrent re-fire · collapsed in de-duped'] : []),
      hasDeduped
        ? `Cumulative: ${fmtR(point.cumulativeSavedR)} (raw) · ${fmtR(point.cumulativeSavedRDeduped)} (de-duped)`
        : `Cumulative: ${fmtR(point.cumulativeSavedR)}`,
    ],
  };
};

/**
 * Chart.js tooltip config for that chart: one tooltip per skip, whichever line
 * is under the cursor or still shown in the legend. Hiding Raw leaves only the
 * de-duped item, and the filter can leave none, so neither callback may assume
 * a particular dataset or a non-empty list.
 */
export const skipChartTooltip = (series, { statusOf, hasDeduped }) => {
  const tooltipFor = (i) => skipTooltip(series[i], { status: statusOf(series[i]), hasDeduped });
  return {
    mode: 'index',
    intersect: false,
    filter: (item, i) => i === 0,
    callbacks: {
      title: (items) => (items.length ? tooltipFor(items[0].dataIndex).title : ''),
      label: (ctx) => tooltipFor(ctx.dataIndex).lines,
    },
  };
};
